import { useState, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ArrowUpWideNarrow,
  ArrowDownWideNarrow,
  ListFilterIcon,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import { Skeleton } from "../ui/Skeleton";

export interface Column<T> {
  key: keyof T | string;
  title: string;
  sortable?: boolean;
  filterable?: boolean;
  filterOptions?: string[];
  render?: (item: T) => React.ReactNode;
  width?: string;
}

interface PaginationProps {
  currentPage?: number;
  setCurrentPage?: (page: number) => void;
  totalPages?: number;
  totalItemsCount?: number;
  setTotalPages?: (size: number) => void;
  pageSize?: number;
  setPageSize?: (size: number) => void;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  itemsPerPage?: number;
  searchable?: boolean;
  searchFields?: (keyof T)[];
  onRowClick?: (item: T) => void;
  isLoading?: boolean;
  customFilters?: React.ReactNode;
  filterComponent?: React.ReactNode;
  pagination?: PaginationProps;
}

export function DataTable<T extends { id: string | number }>({
  data,
  columns,
  itemsPerPage = 10,
  onRowClick,
  isLoading = false,
  customFilters,
  filterComponent,
  pagination = {
    currentPage: 1,
    setCurrentPage: () => {},
    totalPages: 1,
    pageSize: itemsPerPage,
    setPageSize: () => {},
  },
}: DataTableProps<T>) {
  const [pageSize, setPageSize] = useState(itemsPerPage);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState<{
    key: keyof T | string;
    direction: "asc" | "desc";
  } | null>(null);
  const [activeFilters, setActiveFilters] = useState<Record<string, string>>(
    {}
  );

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    let processed = [...data];

    Object.entries(activeFilters).forEach(([key, value]) => {
      if (value) {
        processed = processed.filter(
          (item) =>
            String(item[key as keyof T]).toLowerCase() === value.toLowerCase()
        );
      }
    });

    if (sortConfig) {
      processed.sort((a, b) => {
        const aValue = a[sortConfig.key as keyof T];
        const bValue = b[sortConfig.key as keyof T];
        if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
        if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }

    return processed;
  }, [data, searchQuery, sortConfig, activeFilters]);

  const maxVisiblePages = 3;

  const startPage = Math.max(
    1,
    pagination.currentPage - Math.floor(maxVisiblePages / 2)
  );
  const endPage = Math.min(
    pagination.totalPages,
    startPage + maxVisiblePages - 1
  );

  // Ensure the pagination window doesn't exceed the total pages
  const adjustedStartPage = Math.max(1, endPage - maxVisiblePages + 1);

  const handleSort = (key: keyof T | string) => {
    setSortConfig((current) => ({
      key,
      direction:
        current?.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  };
  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-6">
        {customFilters && <>{customFilters}</>}
        <div className="">{filterComponent}</div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="w-full divide-y divide-gray-200 ">
            <thead className="bg-green-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap w-[60px]">
                  S.No.
                </th>
                {columns.map((column) => (
                  <th
                    key={String(column.key)}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                      column.width || ""
                    }`}
                  >
                    <div className="flex items-center space-x-1">
                      <span>{column.title}</span>
                      {column.sortable && (
                        <button
                          onClick={() => handleSort(column.key)}
                          className="focus:outline-none"
                        >
                          {sortConfig?.key === column.key ? (
                            sortConfig.direction === "asc" ? (
                              <ArrowUpWideNarrow className="w-4 h-4" />
                            ) : (
                              <ArrowDownWideNarrow className="w-4 h-4" />
                            )
                          ) : (
                            <ListFilterIcon className="w-4 h-4 text-gray-400" />
                          )}
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                Array.from({ length: pageSize }).map((_, index) => (
                  <tr key={index}>
                    <td className="px-4 py-4">
                      <Skeleton className="h-4 w-8" />
                    </td>
                    {columns.map((column) => (
                      <td key={String(column.key)} className="px-6 py-4">
                        <Skeleton className="h-4 w-full" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <>
                  {filteredAndSortedData?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={columns.length + 1}
                        className="px-4 py-8 text-center h-[70px]"
                      >
                        <div className="flex flex-col items-center justify-center gap-2">
                          <p className="text-lg font-medium">
                            No records found to display
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <>
                      {filteredAndSortedData.map((item, index) => (
                        <tr
                          key={item.id}
                          onClick={() => onRowClick?.(item)}
                          className={
                            onRowClick ? "cursor-pointer hover:bg-gray-50" : ""
                          }
                        >
                          <td className="px-4 py-4 text-sm text-gray-500 font-medium">
                            {((pagination.currentPage || 1) - 1) * (pagination.pageSize || pageSize) + index + 1}
                          </td>
                          {columns.map((column) => (
                            <td key={String(column.key)} className="px-6 py-4">
                              {column.render
                                ? column.render(item)
                                : String(item[column.key as keyof T])}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </>
                  )}
                </>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">Rows per page:</span>
            <select
              value={pagination?.pageSize}
              onChange={(e) => {
                if (pagination) {
                  const newSize = Number(e.target.value);
                  pagination.setPageSize(newSize);
                }
              }}
              className="border rounded px-2 py-1 text-sm"
            >
              {[2, 10, 20, 30, 50].map((size) => (
                <option key={size} value={size}>
                  {size}
                </option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700">
              Page {pagination.currentPage} of {pagination.totalPages}
            </span>
            <div className="flex items-center gap-1">
              {/* First Page Button */}
              <button
                onClick={() => pagination.setCurrentPage(1)}
                disabled={pagination.currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronsLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() =>
                  pagination.setCurrentPage((p) => Math.max(1, p - 1))
                }
                disabled={pagination.currentPage === 1}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              {Array.from(
                { length: endPage - adjustedStartPage + 1 },
                (_, i) => adjustedStartPage + i
              ).map((page) => (
                <button
                  key={page}
                  onClick={() => pagination.setCurrentPage(page)}
                  className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                    pagination.currentPage === page
                      ? " bg-emerald-50 border-emerald-500 text-emerald-600"
                      : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}

              <button
                onClick={() =>
                  pagination.setCurrentPage((p) =>
                    Math.min(pagination.totalPages, p + 1)
                  )
                }
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
              {/* Last Page Button */}
              <button
                onClick={() => pagination.setCurrentPage(pagination.totalPages)}
                disabled={pagination.currentPage === pagination.totalPages}
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-50"
              >
                <ChevronsRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
